import {log, Random} from "@dp-instant/utils";
import {WaitAction} from "@dp-instant/core";
import {Command} from "@game/coregame/interfaces/Command";
import {Decision} from "@game/coregame/interfaces/Decision";
import {DecisionType} from "@game/coregame/interfaces/DecisionType";
import {IBotConfig} from "@game/coregame/interfaces/IBotConfig";
import {TypesGame} from "@game/TypesGame";
import {inject, injectable} from "inversify";
import {Building} from "../entities/Building";
import {BotCalculationLogic} from "./bots/BotCalculationLogic";

class Delay {
    constructor(private delayMs = 1000, private _startTime = Date.now()) {}
    get running(): boolean {
        return Date.now() - this._startTime < this.delayMs;
    }
}

@injectable()
export class BotLogic {
    @inject(TypesGame.botCalculationLogic)
    private _logic: BotCalculationLogic;
    private _lastCalculationTimestamp = Date.now();
    private _startDelay: Delay;
    private _waitDelay: Delay;
    private readonly _sendingDelay = {x: 0.1, y: 0.5};
    private _preset: IBotConfig;
    private _executingDecision: bool = false;
    private _building: Building;
    public get initialized() {
        return typeof this._preset !== "undefined";
    }

    init(preset: IBotConfig): this {
        this._preset = preset;
        this._logic.init(this._preset);
        return this;
    }

    public decide(building: Building): Command | undefined {
        if (!this._preset) {
            return;
        }
        this._building = building;
        this._startDelay ||= new Delay(Random.range(3) * 1000);
        if (this._startDelay.running || this._logic.busy) {
            return;
        }

        if (this.isReadyForNextDecision) {
            this._lastCalculationTimestamp = Date.now();
            this._logic.calculate(building);
            this._executingDecision = false;
        }
        return this._logic.getLastCommand();
    }
    public executeDecision(decision: Decision) {
        if (this._executingDecision) {
            return;
        }
        if (decision.Type === DecisionType.Move) {
            const moveRoutine = async () => {
                const target = decision.Subject;
                const sources = decision.Objects;
                for (const source of sources) {
                    if (source == target) continue;
                    if (source.owner !== this._building.owner) continue;
                    // log.trace(`#bot ${source.stateId} execute move to ${target.stateId}`);
                    this._building.sendTo(target);
                    await WaitAction.sec(
                        Random.rangeFloat(this._sendingDelay.x, this._sendingDelay.y)
                    );
                    if (!this._executingDecision) {
                        break;
                    }
                }
                this._executingDecision = false;
            };
            this._executingDecision = true;
            moveRoutine();
        } else {
            log.info("executeDecision", DecisionType[decision.Type]);
        }
    }
    public get isReadyForNextDecision() {
        return (
            Date.now() - this._lastCalculationTimestamp >=
            this._preset.waitTimeBetweenDecisions * 1000
        );
    }
    public terminate() {
        this._logic.terminate();
    }
}
